import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["actionIdsToExclude"];
import { useCallback, useMemo } from 'react';
import { Dropdown, DropdownDivider } from 'foundations-components/transitional/components/Dropdown';
import { List } from 'foundations-components/transitional/components/List';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useUserInfo } from 'marketing-platform-lib/providers/UserInfoProvider';
import { useAssetActions } from 'asset-actions-lib/context/AssetActionsContext';
import { groupActions } from 'asset-actions-lib/utils/actions';
import AssetActionButtonComponent from 'asset-actions-lib/internal/components/AssetActionButtonComponent';
import { useTrackAssetActionInteraction } from '../hooks/useTrackAssetActionInteraction';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function useDefaultDropdownButtonProps() {
  const {
    layoutUse
  } = useAssetActions();
  switch (layoutUse) {
    default:
    case 'detail-actions':
      return {
        buttonSize: 'small',
        buttonUse: 'secondary'
      };
    case 'editor-actions':
    case 'hover-actions':
      return {
        buttonSize: 'xs',
        buttonUse: 'tertiary-light'
      };
  }
}
export default function AssetActionsDropdown(_ref) {
  let {
      actionIdsToExclude = []
    } = _ref,
    dropdownProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    scopes,
    gates
  } = useUserInfo();
  const {
    crmObject,
    nonCrmObject,
    layoutUse,
    handleActionClick = () => {},
    actionsWithOverrides = []
  } = useAssetActions();
  const trackAssetActionInteraction = useTrackAssetActionInteraction();
  const defaultDropdownButtonProps = useDefaultDropdownButtonProps();
  const actionGroups = useMemo(() => groupActions({
    actions: actionsWithOverrides.filter(action => !actionIdsToExclude.includes(action.id)),
    crmObject,
    gates,
    layoutUse,
    nonCrmObject,
    scopes
  }), [actionsWithOverrides, actionIdsToExclude, crmObject, gates, layoutUse, nonCrmObject, scopes]);
  const onOpenChangeHandler = useCallback(event => {
    if (event.target.value) {
      trackAssetActionInteraction({
        action: 'open-dropdown',
        crmObject,
        nonCrmObject
      });
    }
    if (dropdownProps && dropdownProps.onOpenChange) {
      dropdownProps.onOpenChange(event);
    }
  }, [crmObject, dropdownProps, nonCrmObject, trackAssetActionInteraction]);
  if (!actionGroups.length) {
    return null;
  }
  const {
    objectId,
    objectTypeId
  } = crmObject;
  return /*#__PURE__*/_jsx("div", {
    "data-test-id": "asset-actions-dropdown",
    "data-coaching-tip": `asset-action-lib-${objectTypeId}-${objectId}-dropdown`,
    children: /*#__PURE__*/_jsx(Dropdown, Object.assign({
      buttonText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "assetActionsLib.sharedActions.actions"
      })
    }, defaultDropdownButtonProps, dropdownProps, {
      onOpenChange: onOpenChangeHandler,
      children: actionGroups.map((actionGroup, idx) => /*#__PURE__*/_jsx(ActionGroup, {
        actionGroup: actionGroup,
        handleClick: handleActionClick,
        shouldRenderDivider: idx !== actionGroups.length - 1
      }, idx))
    }))
  });
}
function ActionGroup({
  actionGroup,
  handleClick,
  shouldRenderDivider
}) {
  const actions = actionGroup.map(action => {
    return /*#__PURE__*/_jsx(AssetActionButtonComponent, {
      action: action,
      onClick: handleClick
    }, action.id);
  }).filter(Boolean);
  return /*#__PURE__*/_jsxs(List, {
    wrapChildren: true,
    children: [actions, shouldRenderDivider && /*#__PURE__*/_jsx(DropdownDivider, {
      "data-test-id": "asset-actions-dropdown-divider"
    })]
  });
}